/**
 * PublicAppUrlCard — super-admin-only widget for setting the platform-wide
 * "Primary Public Application URL". This URL is injected into every
 * outbound email action link (account confirmation, invite, password
 * reset, verification) so links never end up as ``http:///confirm-email``.
 *
 * Distinct from workplace Email Sender settings: workspaces control their
 * own from/reply-to/template, but they cannot see or change this URL.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Globe, CheckCircle, WarningCircle, ShieldStar } from "@phosphor-icons/react";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";

export default function PublicAppUrlCard() {
  const [value, setValue] = useState("");
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/platform/public-app-url");
      setMeta(data);
      // Prefer stored value; fall back to prefill suggestion for first-time
      // setup so the field isn't blank.
      setValue(data.value || data.prefill_suggestion || "");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setError(null);
    if (!value.trim()) { setError("URL is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.put("/platform/public-app-url", { value: value.trim() });
      setMeta(data);
      setValue(data.value);
      toast.success("Public URL saved");
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const configured = !!meta?.is_configured;
  const currentValue = meta?.value;
  const changed = (currentValue || "") !== value.trim();

  return (
    <Card className="mb-4 border-2 border-primary/20" data-testid="public-app-url-card">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <Globe size={18} weight="duotone" className="text-primary" />
          Primary Public Application URL
          <Badge variant="outline" className="ml-auto text-[10px] uppercase gap-1">
            <ShieldStar size={10} weight="fill" /> Super Admin only
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground pt-1">
          This URL is used as the destination for account confirmation,
          invitation, password reset, and other links sent by email.
          Workplace email sender settings cannot change this destination.
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="public-url-input" className="text-xs uppercase tracking-wider text-muted-foreground">
            Domain
          </Label>
          <div className="flex gap-2">
            <Input
              id="public-url-input"
              value={value}
              onChange={(e) => { setValue(e.target.value); setError(null); }}
              placeholder="https://therapystack.pro"
              disabled={loading || saving}
              data-testid="public-url-input"
              className="font-mono text-sm"
            />
            <Button onClick={save} disabled={loading || saving || !changed}
                    data-testid="public-url-save-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
          {error && (
            <p className="text-xs text-rose-600 flex items-center gap-1"
               data-testid="public-url-error">
              <WarningCircle size={12} weight="fill" /> {error}
            </p>
          )}
        </div>

        {configured && (
          <div className="flex items-center gap-2 text-xs" data-testid="public-url-status">
            <CheckCircle size={14} weight="fill" className="text-emerald-600" />
            <span className="text-muted-foreground">
              Active: <span className="font-mono text-foreground">{currentValue}</span>
            </span>
            {meta?.updated_at && (
              <span className="text-muted-foreground ml-auto">
                Last saved {new Date(meta.updated_at).toLocaleString()}
              </span>
            )}
          </div>
        )}
        {!configured && !loading && (
          <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
            <WarningCircle size={14} weight="fill" />
            <span>
              Not configured yet. Emails with action links (confirmation,
              password reset) will be blocked until you save a valid URL.
            </span>
          </div>
        )}
        {meta?.prefill_suggestion && !currentValue && (
          <p className="text-[11px] text-muted-foreground">
            Suggested from environment: <code className="font-mono">{meta.prefill_suggestion}</code>
            {" "}— review it and click Save to activate.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
